import { buildTitle } from "@/lib/seo-quality";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense } from "react";
import { Phone, ArrowRight, CheckCircle2 } from "lucide-react";
import { PageHeader } from "./fordon";
import { FyndanuGrid, FyndanuGridSkeleton, fyndanuListingsQuery } from "@/components/FyndanuGrid";

const URL_SELF = "https://www.akhusbilfritid.se/rapido/begagnade";

const FAQ = [
  {
    q: "Vad ingår i er kontroll av en begagnad Rapido?",
    a: "Fukttest på tak, väggar och golv (viktigast för värdet), chassikoll inklusive rost och bromssystem, funktionsprov på gasol, värme, kylskåp, toalett, dusch, 12 V- och 230 V-system samt en provkörning på minst 3 mil. Det du köper är dokumenterat.",
  },
  {
    q: "Hur mycket faller en Rapido i värde per år?",
    a: "Rapido har en av branschens lägsta värdeminskningar — realistiskt 5–8 % per år efter första året, jämfört med 8–12 % för volymmärken. Väl underhållna bilar med servicehistorik faller ännu långsammare.",
  },
  {
    q: "Kan jag byta in min gamla husbil eller husvagn mot en Rapido?",
    a: "Ja. Vi tar in inbyten oavsett märke. Boka värdering så räknar vi ihop en total där du ser inbytesvärdet, mellanpriset och månadskostnaden om du väljer att finansiera.",
  },
  {
    q: "Har ni finansiering på begagnade Rapido?",
    a: "Ja, via etablerade finansbolag. Räntor och löptid varierar; vi hjälper dig hitta den bästa lösningen — kontantinsats från 20 % är normalt men går att förhandla.",
  },
  {
    q: "Vad ska jag kolla själv på en begagnad Rapido innan köp?",
    a: "Prioriterat: servicehistorik, fuktprotokoll (be alltid att få se det senaste), tätningar runt tak- och sidoluckor, batteriets ålder, gasolregulatorns märkning, samt att alla lucks-, dörr- och skjutfunktioner går smidigt. Vi går igenom allt detta tillsammans med dig innan skrivningen.",
  },
];

export const Route = createFileRoute("/rapido/begagnade")({
  loader: ({ context }) => context.queryClient.ensureQueryData(fyndanuListingsQuery),
  head: () => ({
    meta: [
      { title: buildTitle("Begagnade Rapido husbilar till salu i Kalix") },
      {
        name: "description",
        content:
          "Begagnade Rapido husbilar till salu i Kalix. Kontrollerade, fukttestade, med servicehistorik. Officiell Rapido-återförsäljare. Ring 070-862 97 43.",
      },
      { name: "keywords", content: "begagnade rapido husbilar, rapido husbil till salu, begagnad rapido, rapido v62 begagnad, rapido v68 begagnad, rapido 866f begagnad, rapido kalix" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Begagnade Rapido husbilar till salu – AK Husbil & Fritid" },
      { property: "og:description", content: "Kontrollerade begagnade Rapido husbilar i Kalix. Fukttest, servicehistorik, garantiskydd." },
      { property: "og:url", content: URL_SELF },
    ],
    links: [{ rel: "canonical", href: URL_SELF }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
            { "@type": "ListItem", position: 2, name: "Rapido", item: "https://www.akhusbilfritid.se/rapido" },
            { "@type": "ListItem", position: 3, name: "Begagnade", item: URL_SELF },
          ],
        }),
      },
    ],
  }),
  component: RapidoBegagnade,
});

function RapidoBegagnade() {
  return (
    <>
      <PageHeader
        label="Begagnade Rapido · Kalix"
        title={<>Begagnade <span className="font-italic-display text-accent">Rapido</span> — kontrollerade.</>}
        sub="Vi säljer bara begagnade Rapido vi själva står bakom. Fukttestade, provkörda, med kontrollerad servicehistorik. Live-flödet nedan uppdateras dagligen från vår Fyndanu-butik — försvinner en bil är den ofta redan såld."
      />

      <section className="container-edge -mt-6 pb-4">
        <div className="flex flex-wrap gap-3">
          <a href="tel:0708629743" className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90">
            <Phone size={16} /> Ring 070-862 97 43
          </a>
          <Link to="/rapido/prislista" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            Nyprislista Rapido
          </Link>
        </div>
      </section>

      <section className="container-edge py-12">
        <div className="rounded-3xl border border-border bg-card p-8 md:p-10">
          <p className="eyebrow">Vår kontroll</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">Så vet du vad du köper.</h2>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {[
              "Fukttest på tak, väggar och golv",
              "Full genomgång av gasol- och värmesystem",
              "12 V- och 230 V-el, batteristatus, laddning",
              "Chassi, bromsar, hjul och rostkontroll",
              "Kylskåp, toalett, dusch, kranar och avlopp",
              "Provkörning minst 3 mil innan försäljning",
              "Kontroll av servicehistorik och tätningar",
              "Skriftligt fuktprotokoll till dig som köpare",
            ].map((t) => (
              <div key={t} className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 shrink-0 text-accent" size={20} />
                <span className="text-sm text-foreground/85">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-warm">
        <div className="container-edge py-16 md:py-24">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow">Live-flöde · Fyndanu</p>
              <h2 className="mt-3 font-display text-4xl md:text-5xl">Aktuella <span className="font-italic-display text-accent">begagnade Rapido</span>.</h2>
            </div>
            <a
              href="https://www.fyndanu.se/butik/e435da0b-10d0-46fa-a389-702295c89840"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold text-accent"
            >
              Hela lagret på Fyndanu <ArrowRight size={14} />
            </a>
          </div>
          <div className="mt-10">
            <Suspense fallback={<FyndanuGridSkeleton />}>
              <FyndanuGrid />
            </Suspense>
          </div>
        </div>
      </section>

      <section className="container-edge py-16 md:py-24">
        <p className="eyebrow">Vanliga frågor</p>
        <h2 className="mt-3 font-display text-4xl md:text-5xl">Om <span className="font-italic-display text-accent">begagnade Rapido</span>.</h2>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {FAQ.map((f) => (
            <div key={f.q} className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-display text-xl">{f.q}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
